import * as React from "react"
import { format, subYears, parse, isValid } from "date-fns"
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
  date?: Date
  onSelect?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: (date: Date) => boolean
  disabledPicker?: boolean
  fromYear?: number
  toYear?: number
  className?: string
  /** "default" = profile page style, "dark" = dark modal style (e.g. JoiningBonusModal) */
  variant?: 'default' | 'dark'
}

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
]

const weekdays = ["S", "M", "T", "W", "T", "F", "S"]

export function DatePicker({
  date,
  onSelect,
  placeholder = "Pick a date",
  disabled,
  disabledPicker = false,
  fromYear = 1940,
  toYear = new Date().getFullYear() - 21,
  className,
  variant = 'default'
}: DatePickerProps) {
  const isDark = variant === 'dark'

  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(date)
  const [currentMonth, setCurrentMonth] = React.useState(selectedDate?.getMonth() ?? (new Date().getMonth()))
  const [currentYear, setCurrentYear] = React.useState(selectedDate?.getFullYear() ?? toYear)
  const [isOpen, setIsOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [isTyping, setIsTyping] = React.useState(false)
  const [pickerView, setPickerView] = React.useState<'calendar' | 'year' | 'month'>('calendar')
  const inputRef = React.useRef<HTMLInputElement>(null)
  const yearGridRef = React.useRef<HTMLDivElement>(null)

  // Sync internal state with date prop
  React.useEffect(() => {
    if (date && date instanceof Date && !isNaN(date.getTime())) {
      const dateTime = date.getTime()
      const selectedDateTime = selectedDate?.getTime()
      if (!selectedDate || dateTime !== selectedDateTime) {
        setSelectedDate(date)
        setCurrentMonth(date.getMonth())
        setCurrentYear(date.getFullYear())
        if (!isTyping) {
          setInputValue(format(date, "MM/dd/yyyy"))
        }
      }
    } else if (!date) {
      if (selectedDate) {
        setSelectedDate(undefined)
      }
      if (!isTyping) {
        setInputValue("")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date])

  React.useEffect(() => {
    if (selectedDate && !isTyping) {
      setInputValue(format(selectedDate, "MM/dd/yyyy"))
    }
  }, [selectedDate, isTyping])

  React.useEffect(() => {
    if (isOpen) {
      setPickerView('calendar')
    }
  }, [isOpen])

  React.useEffect(() => {
    if (pickerView === 'year' && yearGridRef.current) {
      const selectedEl = yearGridRef.current.querySelector('[data-selected="true"]')
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'center', behavior: 'instant' })
      }
    }
  }, [pickerView])

  const years = React.useMemo(() => {
    const yearArray = []
    for (let year = toYear; year >= fromYear; year--) {
      yearArray.push(year)
    }
    return yearArray
  }, [fromYear, toYear])

  const getDaysInMonth = (month: number, year: number) => new Date(year, month + 1, 0).getDate()
  const getFirstDayOfMonth = (month: number, year: number) => new Date(year, month, 1).getDay()

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear)
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear)
    const days: { day: number; currentMonth: boolean }[] = []

    if (firstDay > 0) {
      const prevMonthDays = getDaysInMonth(
        currentMonth === 0 ? 11 : currentMonth - 1,
        currentMonth === 0 ? currentYear - 1 : currentYear
      )
      for (let i = firstDay - 1; i >= 0; i--) {
        days.push({ day: prevMonthDays - i, currentMonth: false })
      }
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push({ day, currentMonth: true })
    }

    const remaining = 7 - (days.length % 7)
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        days.push({ day: i, currentMonth: false })
      }
    }

    return days
  }

  const handleDateSelect = (day: number) => {
    const newDate = new Date(currentYear, currentMonth, day)
    const twentyOneYearsAgo = subYears(new Date(), 21)
    if (newDate > twentyOneYearsAgo) return
    if (disabled && disabled(newDate)) return

    setSelectedDate(newDate)
    setInputValue(format(newDate, "MM/dd/yyyy"))
    setIsTyping(false)
    onSelect?.(newDate)
    setIsOpen(false)
  }

  const handleYearSelect = (year: number) => {
    setCurrentYear(year)
    setPickerView('month')
  }

  const handleMonthSelect = (monthIdx: number) => {
    setCurrentMonth(monthIdx)
    setPickerView('calendar')
  }

  const isDateSelected = (day: number) => {
    if (!selectedDate) return false
    return selectedDate.getDate() === day &&
           selectedDate.getMonth() === currentMonth &&
           selectedDate.getFullYear() === currentYear
  }

  const isToday = (day: number) => {
    const today = new Date()
    return day === today.getDate() &&
           currentMonth === today.getMonth() &&
           currentYear === today.getFullYear()
  }

  const isDateDisabled = (day: number) => {
    const testDate = new Date(currentYear, currentMonth, day)
    const twentyOneYearsAgo = subYears(new Date(), 21)
    if (testDate > twentyOneYearsAgo) return true
    if (disabled && disabled(testDate)) return true
    return false
  }

  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11)
      setCurrentYear(prev => prev - 1)
    } else {
      setCurrentMonth(prev => prev - 1)
    }
  }

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0)
      setCurrentYear(prev => prev + 1)
    } else {
      setCurrentMonth(prev => prev + 1)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsTyping(true)
    let value = e.target.value
    value = value.replace(/[^\d/]/g, '')

    const digits = value.replace(/\//g, '')
    if (digits.length <= 2) {
      value = digits
    } else if (digits.length <= 4) {
      value = digits.slice(0, 2) + '/' + digits.slice(2)
    } else {
      value = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4, 8)
    }

    setInputValue(value)

    if (value.length === 10) {
      const parsed = parse(value, "MM/dd/yyyy", new Date())
      if (isValid(parsed)) {
        const twentyOneYearsAgo = subYears(new Date(), 21)
        if (parsed <= twentyOneYearsAgo && parsed.getFullYear() >= fromYear && parsed.getFullYear() <= toYear) {
          if (!disabled || !disabled(parsed)) {
            setSelectedDate(parsed)
            setCurrentMonth(parsed.getMonth())
            setCurrentYear(parsed.getFullYear())
            onSelect?.(parsed)
          }
        }
      }
    }
  }

  const handleInputBlur = () => {
    setIsTyping(false)
    if (selectedDate) {
      setInputValue(format(selectedDate, "MM/dd/yyyy"))
    } else {
      setInputValue("")
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      inputRef.current?.blur()
    }
  }

  // --- Variant-based styles ---
  const triggerClasses = isDark
    ? "bg-white/5 border-white/10 text-white"
    : "bg-muted border-border"

  const dropdownClasses = isDark
    ? "bg-card border-white/10"
    : "bg-background border-border"

  const labelColor = isDark ? "text-amber-400" : "text-primary"
  const textColor = isDark ? "text-white" : ""
  const mutedTextColor = isDark ? "text-white/40" : "text-muted-foreground"
  const placeholderColor = isDark ? "placeholder:text-white/30" : ""
  const hoverBg = isDark ? "hover:bg-white/10" : "hover:bg-accent"
  const hoverText = isDark ? "hover:text-white" : "hover:text-accent-foreground"
  const selectedBg = isDark ? "bg-amber-500 text-amber-950 hover:bg-amber-500" : "bg-primary text-primary-foreground hover:bg-primary"
  const selectedBorder = isDark ? "border border-amber-500/50 text-amber-400" : "border border-primary/50 text-primary"
  const disabledDay = isDark ? "disabled:opacity-20" : "disabled:opacity-30"
  const overflowDay = isDark ? "text-white/15" : "text-muted-foreground/30"

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <PopoverTrigger asChild>
        <div
          className={cn(
            "flex items-center gap-2 w-full rounded-md border px-3 py-2 text-sm ring-offset-background",
            triggerClasses,
            disabledPicker && "opacity-50 cursor-not-allowed",
            !disabledPicker && "cursor-pointer",
            className
          )}
          onClick={() => {
            if (!disabledPicker) {
              setIsOpen(true)
              setTimeout(() => inputRef.current?.focus(), 0)
            }
          }}
        >
          <CalendarIcon className={cn("h-4 w-4 flex-shrink-0", mutedTextColor)} />
          <div className="flex flex-col flex-1 min-w-0">
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              onFocus={() => setIsTyping(true)}
              onClick={(e) => {
                e.stopPropagation()
                if (!isOpen && !disabledPicker) {
                  setIsOpen(true)
                }
              }}
              placeholder={selectedDate ? "" : placeholder}
              disabled={disabledPicker}
              className={cn(
                "bg-transparent border-none outline-none p-0 w-full text-sm cursor-pointer focus:cursor-text",
                selectedDate ? "font-medium" : cn("font-normal", mutedTextColor),
                textColor,
                placeholderColor,
              )}
              maxLength={10}
            />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <div className={cn("p-3 rounded-lg shadow-lg border", dropdownClasses)}>
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => {
                if (pickerView === 'calendar') setPickerView('year')
                else setPickerView('calendar')
              }}
              className={cn(
                "flex items-center gap-1 font-semibold text-sm rounded px-2 py-1 transition-colors",
                hoverBg, textColor
              )}
            >
              <span>{months[currentMonth]} {currentYear}</span>
              {pickerView === 'calendar' ? (
                <ChevronDown className={cn("h-3.5 w-3.5", isDark ? "opacity-40" : "opacity-50")} />
              ) : (
                <ChevronUp className={cn("h-3.5 w-3.5", isDark ? "opacity-40" : "opacity-50")} />
              )}
            </button>
            {pickerView === 'calendar' && (
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); goToPreviousMonth() }}
                  className={cn("h-7 w-7 flex items-center justify-center rounded transition-colors", hoverBg)}
                  aria-label="Previous month"
                >
                  <ChevronLeft className={cn("h-4 w-4", textColor)} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); goToNextMonth() }}
                  className={cn("h-7 w-7 flex items-center justify-center rounded transition-colors", hoverBg)}
                  aria-label="Next month"
                >
                  <ChevronRight className={cn("h-4 w-4", textColor)} />
                </button>
              </div>
            )}
          </div>

          {/* Year picker */}
          {pickerView === 'year' && (
            <div ref={yearGridRef} className="grid grid-cols-4 gap-1 max-h-[252px] overflow-y-auto overscroll-contain py-1" style={{ WebkitOverflowScrolling: 'touch' }}>
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  data-selected={year === currentYear}
                  onClick={() => handleYearSelect(year)}
                  className={cn(
                    "h-9 rounded-lg text-sm font-medium transition-colors",
                    hoverBg, hoverText,
                    year === currentYear && cn(selectedBg, "font-semibold"),
                    year === selectedDate?.getFullYear() && year !== currentYear && selectedBorder,
                  )}
                >
                  {year}
                </button>
              ))}
            </div>
          )}

          {/* Month picker */}
          {pickerView === 'month' && (
            <div className="grid grid-cols-3 gap-1.5 py-1">
              {months.map((month, idx) => {
                const twentyOneYearsAgo = subYears(new Date(), 21)
                const isMonthDisabled = new Date(currentYear, idx, 1) > twentyOneYearsAgo
                return (
                  <button
                    key={idx}
                    type="button"
                    disabled={isMonthDisabled}
                    onClick={() => handleMonthSelect(idx)}
                    className={cn(
                      "h-10 rounded-lg text-sm font-medium transition-colors",
                      hoverBg, hoverText,
                      "disabled:opacity-30 disabled:cursor-not-allowed",
                      idx === currentMonth && cn(selectedBg, "font-semibold"),
                      idx === selectedDate?.getMonth() && currentYear === selectedDate?.getFullYear() && idx !== currentMonth && selectedBorder,
                    )}
                  >
                    {month.slice(0, 3)}
                  </button>
                )
              })}
            </div>
          )}

          {/* Calendar */}
          {pickerView === 'calendar' && (
            <div>
              <div className="grid grid-cols-7 mb-1">
                {weekdays.map((day, i) => (
                  <div key={i} className={cn("h-8 flex items-center justify-center text-xs font-medium", mutedTextColor)}>
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {generateCalendarDays().map((item, index) => (
                  <div key={index} className="h-9 flex items-center justify-center">
                    {item.currentMonth ? (
                      <button
                        type="button"
                        onClick={() => handleDateSelect(item.day)}
                        disabled={isDateDisabled(item.day)}
                        className={cn(
                          "h-8 w-8 rounded-full text-sm transition-colors",
                          hoverBg, hoverText,
                          cn(disabledDay, "disabled:cursor-not-allowed"),
                          isDateSelected(item.day) && cn(selectedBg, "font-semibold"),
                          isToday(item.day) && !isDateSelected(item.day) && selectedBorder,
                          !isDateSelected(item.day) && !isToday(item.day) && !isDateDisabled(item.day) && hoverBg,
                        )}
                      >
                        {item.day}
                      </button>
                    ) : (
                      <span className={cn("h-8 w-8 flex items-center justify-center text-sm", overflowDay)}>
                        {item.day}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
